import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'

import {retryOnCondition} from './retryOnCondition'

export enum RequestStatus {
  DEFAULT = 'DEFAULT',
  LOADING = 'LOADING',
  RESOLVED = 'RESOLVED',
  FAILED = 'FAILED',
}

type WrapRequestFunc = <T, K extends unknown[]>(
  flowAPI: ControllerFlowAPI,
  method: (flowApi: ControllerFlowAPI, ...params: K) => Promise<T>,
  dataFieldName: string,
  statusFieldName: string,
  errorFieldName?: string,
  retryCondition?: (data: T) => boolean,
  maxAttempts?: number,
  delayMillis?: number,
  isSuperseded?: () => boolean,
) => (...params: K) => Promise<T | undefined>

export const wrapRequest: WrapRequestFunc =
  (
    flowAPI,
    method,
    dataFieldName,
    statusFieldName,
    errorFieldName,
    retryCondition,
    maxAttempts = 3,
    delayMillis = 5000,
    isSuperseded = () => false,
  ) =>
  async (...params) => {
    const setProps = flowAPI.controllerConfig.setProps
    const {getResolvedError} = flowAPI.errorHandler

    try {
      setProps({
        [statusFieldName]: RequestStatus.LOADING,
      })

      // Wrapper function to include retry logic
      const fetchDataWithRetry = async () => {
        if (retryCondition) {
          return retryOnCondition({
            fetchData: () => method(flowAPI, ...params),
            checkCondition: retryCondition,
            maxAttempts,
            delayMillis,
          })
        } else {
          return method(flowAPI, ...params)
        }
      }

      const data = await fetchDataWithRetry()

      if (isSuperseded()) {
        return data
      }

      setProps({
        [statusFieldName]: RequestStatus.RESOLVED,
        [dataFieldName]: data,
        ...(errorFieldName ? {[errorFieldName]: undefined} : {}),
      })

      return data
    } catch (error) {
      flowAPI.panoramaClient.errorMonitor().reportError(error as Error)
      const resolvedError = getResolvedError(error)

      if (isSuperseded()) {
        return
      }

      setProps({
        [statusFieldName]: RequestStatus.FAILED,
        ...(errorFieldName
          ? {
              [errorFieldName]: resolvedError,
            }
          : {}),
      })
    }
  }

/**
 * Same as `wrapRequest`, but only the latest call writes its result to storage,
 * so a slow earlier response cannot overwrite a newer one.
 */
export const wrapLatestRequest: WrapRequestFunc = (
  flowAPI,
  method,
  dataFieldName,
  statusFieldName,
  errorFieldName,
  retryCondition,
  maxAttempts,
  delayMillis,
) => {
  let latestRequestId = 0

  return (...params) => {
    const requestId = ++latestRequestId

    return wrapRequest(
      flowAPI,
      method,
      dataFieldName,
      statusFieldName,
      errorFieldName,
      retryCondition,
      maxAttempts,
      delayMillis,
      () => requestId !== latestRequestId,
    )(...params)
  }
}
